import React, { useEffect, useState, FormEvent } from "react";
import { useParams } from "react-router-dom";

import { IModulesPage } from "../../../pages/admin/StudentsPage/StudentDetails/types";
import { IManualPayment } from "./types";
import { formatPaymentMethod } from "./types";

const Payments = () => {
  const { id } = useParams<{ id: string }>();
  const token = localStorage.getItem("token");

  const [isLoading, setIsLoading] = useState(true);
  const privilege = parseInt(localStorage.getItem("privilege") || "0");
  const canModifyPayments = privilege === 1 || privilege === 2;
  const [message, setMessage] = useState("");

  const [payments, setPayments] = useState<IManualPayment[]>([]);
  const [addPayment, setAddPayment] = useState(false);

  const [manualModuleId, setManualModuleId] = useState("");
  const [manualPaymentMethod, setManualPaymentMethod] = useState("");
  const [manualPaidAt, setManualPaidAt] = useState("");
  const [manualReferenceNumber, setManualReferenceNumber] = useState("");
  const [manualAmount, setManualAmount] = useState(0);
  const [modulesList, setModulesList] = useState<IModulesPage[]>([]);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString("es-MX", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const resetFormFields = () => {
    setManualModuleId("");
    setManualPaidAt("");
    setManualAmount(0);
    setManualPaymentMethod("");
    setManualReferenceNumber("");
  };

  useEffect(() => {
    fetch("https://has-backend-c48d669d897f.herokuapp.com/admin/modules", {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setModulesList(data);
      })
      .catch(err => {
        console.error("Error al obtener módulos:", err);
      });
  }, []);

  useEffect(() => {
    fetch(`https://has-backend-c48d669d897f.herokuapp.com/payment/${id}/payments`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then(res => res.json())
      .then(data => {
        setPayments(data);
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Error al obtener pagos:", err);
        setIsLoading(false);
      });
  }, [refreshTrigger]);

  const handleManualPaymentSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setMessage("");

    if (!manualPaymentMethod) {
      setMessage("Selecciona un método de pago válido");
      return;
    }

    const isEdit = payments.some(p => p.moduleId === manualModuleId);
    const url = isEdit
      ? `https://has-backend-c48d669d897f.herokuapp.com/admin/payment/manual/${id}/${manualModuleId}`
      : `https://has-backend-c48d669d897f.herokuapp.com/admin/payment/manual/${id}`;
    const method = isEdit ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          moduleId: manualModuleId,
          paidAt: new Date(manualPaidAt).toISOString(),
          amount: manualAmount,
          paymentMethod: manualPaymentMethod,
          referenceNumber: manualReferenceNumber,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Fallo al guardar pago");

      setAddPayment(false);
      setRefreshTrigger(prev => prev + 1);
      resetFormFields();
    } catch (err: any) {
      setMessage(err.message);
    }
  };

  const handleDeletePayment = async (moduleIdToDelete: string) => {
    try {
      const response = await fetch(
        `https://has-backend-c48d669d897f.herokuapp.com/admin/payment/manual/${id}/${moduleIdToDelete}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Fallo al eliminar pago");

      setRefreshTrigger(prev => prev + 1);
    } catch (err: any) {
      setMessage(err.message);
    }
  };

  return (
    <div className="flex flex-col items-start gap-8 w-full mt-8">
      <div className="w-full flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h2 className="font-playfair font-bold text-3xl sm:text-4xl">Historial de pago</h2>
        {canModifyPayments && (
          <button
            onClick={() => {
              resetFormFields();
              setAddPayment(true);
            }}
            className="bg-[#1c1c2e] border border-[#8E8E8E] rounded-lg text-white font-sourceSans w-full sm:w-1/4 shadow-lg py-2"
          >
            Registrar pago
          </button>
        )}
      </div>

      {addPayment && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-[#d9d9d9] w-[90%] sm:w-2/4 max-h-[90%] overflow-y-auto p-6 rounded-lg shadow-lg relative">
            <button
              className="absolute top-2 right-2 text-gray-800 text-lg"
              onClick={() => {
                setAddPayment(false);
                resetFormFields();
              }}
            >
              x
            </button>
            <form onSubmit={handleManualPaymentSubmit} className="w-full flex flex-col gap-3">
              <label>Módulo</label>
              <select value={manualModuleId} onChange={(e) => setManualModuleId(e.target.value)} required>
                <option value="">Selecciona un módulo</option>
                {[...modulesList]
                  .sort((a, b) => Number(a.week) - Number(b.week))
                  .map((m: any) => (
                    <option key={m._id} value={m._id}>
                      {m.title} — Semana {m.week}
                    </option>
                ))}
              </select>

              <label>Tipo de pago</label>
              <select value={manualPaymentMethod} onChange={(e) => setManualPaymentMethod(e.target.value)} >
                <option value="">Selecciona un método</option>
                <option value="transferencia">Transferencia</option>
                <option value="deposito">Depósito</option>
                <option value="efectivo">Efectivo</option>
              </select>

              <label>Fecha</label>
              <input type="date" value={manualPaidAt} onChange={(e) => setManualPaidAt(e.target.value)}  />

              <label>Número de referencia</label>
              <input type="text" value={manualReferenceNumber} onChange={(e) => setManualReferenceNumber(e.target.value)}  />

              <label>Monto</label>
              <input type="number" value={manualAmount} onChange={(e) => setManualAmount(Number(e.target.value))}  />

              <button type="submit" className="mt-4 bg-[#1c1c2e] text-white py-2 rounded-md">
                Guardar pago
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="w-full flex flex-col">
        {isLoading ? (
          <p>Cargando pagos...</p>
        ) : payments.length > 0 ? (
          payments.map((p, i) => (
            <div key={i} className="flex flex-col sm:flex-row justify-between border-y border-gray-600 py-2 px-4 sm:px-8">
              <p className="font-sourceSans text-sm sm:text-base">
                El estudiante pagó el módulo <strong>{p.title}</strong> correspondiente a la{" "}
                <strong>semana {p.week}</strong> el día{" "}
                <strong>{formatDate(p.paidAt)}</strong> mediante{" "}
                <strong>{formatPaymentMethod(p.paymentMethod)}</strong>. Monto: <strong>${p.amount} MXN</strong>
              </p>
              {canModifyPayments && (
                <div className="flex flex-col sm:flex-row gap-2 mt-2 sm:mt-0">
                  <button
                    onClick={() => {
                      setManualModuleId(p.moduleId);
                      setManualPaidAt(p.paidAt.slice(0, 10));
                      setManualAmount(p.amount);
                      setManualPaymentMethod(p.paymentMethod);
                      setManualReferenceNumber(p.referenceNumber);
                      setAddPayment(true);
                    }}
                    className="bg-[#1c1c2e] text-white text-sm px-4 py-1 rounded-md"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDeletePayment(p.moduleId)}
                    className="bg-red-600 text-white text-sm px-4 py-1 rounded-md"
                  >
                    Eliminar
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          <p className="font-sourceSans px-4">El estudiante no ha realizado pagos todavía.</p>
        )}
      </div>

      {message && <p className="text-red-600 font-sourceSans px-4">{message}</p>}
    </div>
  );
};

export default Payments;
